import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import DirectionsWalkRoundedIcon from '@mui/icons-material/DirectionsWalkRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../../services/reports.service';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { StatCard } from '../../components/common/StatCard';
import { LoadingSkeleton } from '../../components/common/LoadingSkeleton';
import { PageHeader } from '../../components/common/PageHeader';

export function TenantDashboard() {
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['tenant-dashboard'],
    queryFn: () => reportsService.getTenantDashboardStats(),
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingSkeleton variant="page" cardCount={4} />;
  if (error) return <ErrorMessage message="Failed to load dashboard" onRetry={refetch} />;

  const totalEmployees = stats?.totalEmployees ?? 0;
  const activeToday = stats?.activeToday ?? 0;
  const completed = stats?.visitsCompleted ?? 0;
  const pending = stats?.pendingVisits ?? 0;
  const interruptions = stats?.trackingInterruptions ?? 0;
  const assigned = completed + pending;
  const rate = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
  const offDuty = Math.max(0, totalEmployees - activeToday);

  const todayLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box>
      <PageHeader
        compact
        title="Dashboard"
        subtitle={`Overview of today's field operations · ${todayLabel}`}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Employees"
            value={totalEmployees}
            subtitle="Total field force"
            icon={<PeopleRoundedIcon />}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Today"
            value={activeToday}
            subtitle="On duty now"
            icon={<DirectionsWalkRoundedIcon />}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Visits Completed"
            value={completed}
            subtitle={`${pending} still pending`}
            icon={<CheckCircleRoundedIcon />}
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tracking Alerts"
            value={interruptions}
            subtitle={interruptions > 0 ? 'Needs attention' : 'All clear'}
            icon={<WarningAmberRoundedIcon />}
            tone={interruptions > 0 ? 'error' : 'neutral'}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom letterSpacing="-0.01em">
                Today&apos;s Visit Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Completion across all assigned visits
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Completion rate
                </Typography>
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {rate}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={rate}
                sx={{ height: 10, borderRadius: 5, mb: 3 }}
                aria-label={`Visit completion ${rate} percent`}
              />
              <Grid container spacing={2}>
                {[
                  { label: 'Completed', value: completed, color: 'success.main' },
                  { label: 'Pending', value: pending, color: 'warning.main' },
                  { label: 'Assigned', value: assigned, color: 'text.primary' },
                ].map((item) => (
                  <Grid item xs={4} key={item.label}>
                    <Box
                      textAlign="center"
                      sx={{
                        py: 1.5,
                        px: 1,
                        borderRadius: 2,
                        bgcolor: 'neutral.50',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="h5" fontWeight={800} color={item.color} letterSpacing="-0.02em">
                        {item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom letterSpacing="-0.01em">
                Workforce
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Duty status across your team
              </Typography>
              <List disablePadding>
                <ListItem
                  sx={{
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: 'neutral.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                  }}
                >
                  <ListItemText
                    primary="Active on duty"
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                  />
                  <Chip label={activeToday} color="success" size="small" />
                </ListItem>
                <ListItem
                  sx={{
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: 'neutral.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <ListItemText
                    primary="Off duty"
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                  />
                  <Chip label={offDuty} size="small" />
                </ListItem>
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Stats refresh automatically every minute
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
